// src/v3/CardButton/index.tsx
import { cva } from "class-variance-authority";
import {
  useContext,
  forwardRef
} from "react";

// src/contexts/ComponentProvider.tsx
import { createContext, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
var GLOBAL_KEY = "__@jouzen/ecom-components:ComponentContext__";
function getOrCreateContext() {
  const g = globalThis;
  if (!g[GLOBAL_KEY])
    g[GLOBAL_KEY] = createContext({});
  return g[GLOBAL_KEY];
}
var Context = getOrCreateContext();
function ComponentProvider({
  children,
  LinkComponent,
  ImageComponent
}) {
  const contextValue = useMemo(
    () => ({ ImageComponent, LinkComponent }),
    [ImageComponent, LinkComponent]
  );
  return /* @__PURE__ */ jsx(Context.Provider, {
    value: contextValue,
    children
  });
}
ComponentProvider.displayName = "ComponentProvider";

// src/icons/CardBtnArrowIcon.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function CardBtnArrowIcon({
  title,
  titleId,
  ...props
}) {
  return /* @__PURE__ */ jsxs("svg", {
    "aria-hidden": "true",
    "aria-labelledby": titleId,
    fill: "none",
    height: 13,
    viewBox: "0 0 13 13",
    width: 13,
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      title ? /* @__PURE__ */ jsx2("title", {
        id: titleId,
        children: title
      }) : null,
      /* @__PURE__ */ jsx2("path", {
        d: "M12.5303 6.53033C12.8232 6.23744 12.8232 5.76256 12.5303 5.46967L7.75736 0.696699C7.46447 0.403806 6.98959 0.403806 6.6967 0.696699C6.40381 0.989593 6.40381 1.46447 6.6967 1.75736L10.9393 6L6.6967 10.2426C6.40381 10.5355 6.40381 11.0104 6.6967 11.3033C6.98959 11.5962 7.46447 11.5962 7.75736 11.3033L12.5303 6.53033ZM0 6.75L12 6.75V5.25L0 5.25L0 6.75Z",
        fill: "currentColor"
      })
    ]
  });
}

// src/icons/CardExpandCloseIcon.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function CardExpandCloseIcon({
  title,
  titleId,
  ...props
}) {
  return /* @__PURE__ */ jsxs2("svg", {
    "aria-hidden": "true",
    "aria-labelledby": titleId,
    fill: "none",
    height: 13,
    viewBox: "0 0 13 13",
    width: 13,
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      title ? /* @__PURE__ */ jsx3("title", {
        id: titleId,
        children: title
      }) : null,
      /* @__PURE__ */ jsx3("rect", {
        fill: "currentColor",
        height: 13,
        transform: "rotate(45 12.5356 4.05023)",
        width: 2,
        x: 8.5,
        y: 3.5
      }),
      /* @__PURE__ */ jsx3("rect", {
        fill: "currentColor",
        height: 13,
        transform: "rotate(135 13.9497 12.5355)",
        width: 2,
        x: 13.9497,
        y: 16
      })
    ]
  });
}

// src/icons/CardExpandOpenIcon.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function CardExpandOpenIcon({
  title,
  titleId,
  ...props
}) {
  return /* @__PURE__ */ jsxs3("svg", {
    "aria-hidden": "true",
    "aria-labelledby": titleId,
    fill: "none",
    height: 13,
    viewBox: "0 0 13 13",
    width: 13,
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      title ? /* @__PURE__ */ jsx4("title", {
        id: titleId,
        children: title
      }) : null,
      /* @__PURE__ */ jsx4("rect", {
        fill: "currentColor",
        height: 13,
        width: 2,
        x: 5.5,
        y: 0
      }),
      /* @__PURE__ */ jsx4("rect", {
        fill: "currentColor",
        height: 13,
        transform: "rotate(90 13 5)",
        width: 2,
        x: 13,
        y: 5
      })
    ]
  });
}

// src/v3/CardButton/index.tsx
import { Fragment, jsx as jsx5 } from "react/jsx-runtime";
var cardButtonVar = cva(
  "ui-rounded-full ui-text-center ui-inline-block ui-transition",
  {
    variants: {
      color: {
        light: "ui-bg-sandstone-200 ui-text-sandstone-500",
        dark: "ui-bg-sandstone-500 ui-text-sandstone-200",
        "brown-500": "ui-bg-brown-500 ui-text-sandstone-200",
        "slate-600": "ui-bg-slate-600 ui-text-sandstone-200",
        "gray-600": "ui-bg-gray-600 ui-text-sandstone-200",
        "olive-600": "ui-bg-olive-600 ui-text-sandstone-200",
        "purple-600": "ui-bg-purple-600 ui-text-sandstone-200",
        "mustard-700": "ui-bg-mustard-700 ui-text-sandstone-200"
      },
      size: {
        small: "ui-p-2.5",
        large: "ui-p-4",
        xlarge: "ui-p-5"
      }
    },
    defaultVariants: {
      color: "dark",
      size: "large"
    }
  }
);
var CardButton = forwardRef(
  function CardButton2({
    ariaLabel = "",
    color,
    external,
    href,
    size,
    variant = "arrow",
    className,
    open,
    ...props
  }, ref) {
    const context = useContext(Context);
    const LinkComponent = external || !context.LinkComponent ? "a" : context.LinkComponent;
    if (href) {
      return /* @__PURE__ */ jsx5(LinkComponent, {
        className: cardButtonVar({
          color,
          size,
          className
        }),
        "data-cy": "card-button-link",
        href,
        ...props,
        ref,
        children: external ? /* @__PURE__ */ jsx5(CardBtnArrowIcon, {
          "aria-hidden": true,
          className: "ui--rotate-45"
        }) : /* @__PURE__ */ jsx5(CardBtnArrowIcon, {
          "aria-hidden": true
        })
      });
    }
    return /* @__PURE__ */ jsx5("button", {
      "aria-label": ariaLabel,
      className: cardButtonVar({
        color,
        size,
        className
      }),
      "data-cy": "card-button",
      ref,
      type: "button",
      ...props,
      children: variant === "expand" ? /* @__PURE__ */ jsx5(Fragment, {
        children: open ? /* @__PURE__ */ jsx5("span", {
          "data-cy": "expand-close",
          children: /* @__PURE__ */ jsx5(CardExpandCloseIcon, {
            "aria-hidden": true
          })
        }) : /* @__PURE__ */ jsx5("span", {
          "data-cy": "expand-open",
          children: /* @__PURE__ */ jsx5(CardExpandOpenIcon, {
            "aria-hidden": true
          })
        })
      }) : /* @__PURE__ */ jsx5(CardBtnArrowIcon, {
        "aria-hidden": true
      })
    });
  }
);
CardButton.displayName = "CardButton";
export {
  CardButton
};
//# sourceMappingURL=index.mjs.map